/**
 * Backend API client for Cloudflare Workers backend
 */

import type {
  User,
  Agent,
  CreateAgentResponse,
  ConfigKey,
  ConfigGetResponse,
  ConfigListResponse,
} from './types.js';
import type { AuthConfig } from '../config.js';
import { DEFAULT_API_URL } from '@spacecode/shared/remote';

/**
 * Request timeout in milliseconds
 */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Custom error classes for API errors
 */
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export class RateLimitError extends Error {
  retryAfter: Date;
  constructor(message: string, retryAfter: Date) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

export class ApiError extends Error {
  status: number;
  type?: string;
  constructor(message: string, status: number, type?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.type = type;
  }
}

/**
 * Backend API client
 */
export class BackendApiClient {
  private baseUrl: string;
  private token?: string;

  constructor(baseUrl?: string, token?: string) {
    this.baseUrl = baseUrl ?? process.env.SPACECODE_API_URL ?? DEFAULT_API_URL;
    this.token = token;
  }

  /**
   * Set JWT token for authentication
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Get current token
   */
  getToken(): string | undefined {
    return this.token;
  }

  /**
   * Clear token (logout)
   */
  clearToken(): void {
    this.token = undefined;
  }

  /**
   * Check if token is set and potentially valid
   * Note: This doesn't validate with the server, just checks local state
   */
  hasToken(): boolean {
    return !!this.token;
  }

  /**
   * Make an authenticated API request
   */
  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> | undefined),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Interim shared-token gate on the backend's /api/* (RL-A0); Authorization carries the user's token.
    const sharedToken = process.env.SPACECODE_SHARED_TOKEN;
    if (sharedToken) {
      headers['X-Spacecode-Token'] = sharedToken;
    }

    // Set content-type if body exists
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        this.clearToken();
        throw new AuthError('Authentication failed. Please check your API key or run a command to re-authenticate');
      }

      // Handle 429 Rate Limit
      if (response.status === 429) {
        const errorData = await response.json().catch(() => ({}));
        const retryAfter = errorData.retryAfter
          ? new Date(errorData.retryAfter)
          : new Date(Date.now() + 120_000); // Default 2 minutes

        throw new RateLimitError(
          errorData.error ?? 'Rate limit exceeded. Please try again later.',
          retryAfter
        );
      }

      // Handle other errors
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.status}`;
        let errorType: string | undefined;
        try {
          const errorData = JSON.parse(errorText) as { error?: string; type?: string };
          errorMessage = errorData.error ?? errorMessage;
          errorType = errorData.type;
        } catch {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
        throw new ApiError(errorMessage, response.status, errorType);
      }

      return await response.json() as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof AuthError || error instanceof RateLimitError || error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout - API took too long to respond', 408);
      }

      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error',
        0
      );
    }
  }

  /**
   * Health check - verify backend is accessible
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        signal: AbortSignal.timeout(5000),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    return this.request<User>('/api/auth/me');
  }

  /**
   * List all configs
   */
  async getConfigs(): Promise<ConfigListResponse> {
    return this.request<ConfigListResponse>('/api/config');
  }

  /**
   * Get a specific config
   */
  async getConfig(key: ConfigKey): Promise<ConfigGetResponse> {
    return this.request<ConfigGetResponse>(`/api/config/${key}`);
  }

  /**
   * Set a config
   */
  async setConfig(key: ConfigKey, content: string): Promise<{ success: boolean; key: string; path: string }> {
    return this.request(`/api/config/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Delete a config
   */
  async deleteConfig(key: ConfigKey): Promise<{ success: boolean }> {
    return this.request(`/api/config/${key}`, {
      method: 'DELETE',
    });
  }

  /**
   * Bulk set configs
   */
  async setConfigs(configs: Record<string, string>): Promise<{ success: boolean; updated: string[] }> {
    return this.request('/api/config', {
      method: 'PUT',
      body: JSON.stringify(configs),
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Create a new agent account
   */
  async createAgent(name: string, options?: { description?: string; webhookUrl?: string }): Promise<CreateAgentResponse> {
    return this.request('/api/agents', {
      method: 'POST',
      body: JSON.stringify({ name, ...options }),
    });
  }

  /**
   * List all agents owned by the current user
   */
  async listAgents(): Promise<{ agents: Agent[] }> {
    return this.request('/api/agents');
  }

  /**
   * Get a specific agent by ID
   */
  async getAgent(id: string): Promise<{ agent: Agent }> {
    return this.request(`/api/agents/${id}`);
  }

  /**
   * Update an agent's configuration
   */
  async updateAgent(id: string, updates: { description?: string; webhookUrl?: string; maxConcurrentSandboxes?: number }): Promise<{ agent: Agent }> {
    return this.request(`/api/agents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete an agent
   */
  async deleteAgent(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/agents/${id}`, {
      method: 'DELETE',
    });
  }

  // -------------------------------------------------------------------------
  // Sentry integration
  // -------------------------------------------------------------------------

  async setSentryCredentials(authToken: string, orgSlug: string): Promise<{ success: boolean }> {
    return this.request('/api/credentials/sentry', {
      method: 'PUT',
      body: JSON.stringify({ authToken, orgSlug }),
    });
  }

  async getSentryStatus(): Promise<{ hasToken: boolean; orgSlug?: string }> {
    return this.request('/api/credentials/sentry');
  }

  async deleteSentryCredentials(): Promise<{ success: boolean }> {
    return this.request('/api/credentials/sentry', { method: 'DELETE' });
  }

  async listSentryProjects(): Promise<{ projects: Array<{ id: string; slug: string; name: string; platform: string | null }> }> {
    return this.request('/api/sentry/projects');
  }

  async listSentryIssues(project?: string, limit?: number): Promise<{ issues: Array<Record<string, unknown>> }> {
    const params = new URLSearchParams();
    if (project) params.set('project', project);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return this.request(`/api/sentry/issues${qs ? `?${qs}` : ''}`);
  }

  async fixSentryIssue(issueId: string, repo: string, options?: { branch?: string; model?: string }): Promise<Record<string, unknown>> {
    return this.request('/api/sentry/fix', {
      method: 'POST',
      body: JSON.stringify({ issueId, repo, ...options }),
    });
  }

  async fixSentryBatch(issueIds: string[], repo: string, model?: string): Promise<{ results: Array<Record<string, unknown>> }> {
    return this.request('/api/sentry/fix-batch', {
      method: 'POST',
      body: JSON.stringify({ issueIds, repo, model }),
    });
  }

  // Sentry webhook management
  async listSentryWebhooks(): Promise<{ webhooks: Array<Record<string, unknown>> }> {
    return this.request('/api/sentry/webhooks');
  }

  async createSentryWebhook(
    sentryProjectSlug: string,
    githubRepo: string,
    model?: string,
  ): Promise<{ webhook: Record<string, unknown>; webhookSecret: string }> {
    return this.request('/api/sentry/webhooks', {
      method: 'POST',
      body: JSON.stringify({ sentryProjectSlug, githubRepo, model }),
    });
  }

  async deleteSentryWebhook(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/sentry/webhooks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async toggleSentryWebhook(id: string, enabled: boolean): Promise<{ success: boolean }> {
    return this.request(`/api/sentry/webhooks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ autoFixEnabled: enabled }),
    });
  }

  async getSentryWebhookDeliveries(id: string, limit = 50): Promise<{ deliveries: Array<Record<string, unknown>> }> {
    return this.request(`/api/sentry/webhooks/${encodeURIComponent(id)}/deliveries?limit=${limit}`);
  }

  // Sentry connect (project + webhook in one call)
  async sentryConnect(
    repo: string,
    projectSlug?: string,
    model?: string,
  ): Promise<Record<string, unknown>> {
    return this.request('/api/sentry/connect', {
      method: 'POST',
      body: JSON.stringify({ repo, projectSlug, model }),
    });
  }

  // Sentry OAuth
  async startSentryOAuth(): Promise<{ url: string; state: string }> {
    return this.request('/api/sentry/oauth/start', { method: 'POST' });
  }

  async pollSentryOAuth(state: string): Promise<{ completed: boolean; orgSlug?: string; error?: string }> {
    return this.request(`/api/sentry/oauth/status?state=${encodeURIComponent(state)}`);
  }
}

/**
 * Create an API client with token from config
 */
export async function createApiClient(): Promise<BackendApiClient> {
  const { loadAuth, loadConfig } = await import('../config.js');
  const auth = await loadAuth();
  const config = await loadConfig();
  const apiUrl = process.env.SPACECODE_API_URL ?? (config.apiUrl || DEFAULT_API_URL);

  return new BackendApiClient(apiUrl, auth.accessToken);
}

/**
 * Verify token is valid by checking if we can fetch user info
 */
export async function isTokenValid(client: BackendApiClient): Promise<boolean> {
  if (!client.hasToken()) {
    return false;
  }

  try {
    await client.getCurrentUser();
    return true;
  } catch (error) {
    if (error instanceof AuthError) {
      return false;
    }
    throw error;
  }
}
